import React, { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";

/**
 * Lightweight "Was this article helpful?" voter.
 * Persists the choice in localStorage per slug so the user can't spam-vote.
 * Aggregated counts could later be wired to a backend (`POST /api/blog/{slug}/vote`).
 */
export default function BlogHelpfulVote({ slug }) {
  const storageKey = `blog_vote_${slug}`;
  const [vote, setVote] = useState(null); // "up" | "down" | null

  useEffect(() => {
    try { setVote(localStorage.getItem(storageKey)); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const cast = (choice) => {
    if (vote) return;
    setVote(choice);
    try { localStorage.setItem(storageKey, choice); } catch { /* ignore quota */ }
  };

  if (vote) {
    return (
      <div className="mt-12 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="blog-vote-thanks">
        <Check size={20} className="text-emerald-600 inline-block mr-1" />
        <span className="text-sm font-semibold text-emerald-800">
          Merci pour ton retour {vote === "up" ? "👍" : "👎"} !
        </span>
        <p className="text-xs text-emerald-700 mt-1">Tes votes nous aident à améliorer le blog.</p>
      </div>
    );
  }

  return (
    <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-5 text-center" data-testid="blog-helpful-vote">
      <p className="text-sm font-semibold text-slate-900 mb-3">Cet article t'a aidé·e ?</p>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => cast("up")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 transition-colors text-sm font-semibold"
          data-testid="blog-vote-up"
          aria-label="Oui, cet article m'a aidé"
        >
          <ThumbsUp size={14} /> Oui
        </button>
        <button
          onClick={() => cast("down")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-rose-300 hover:bg-rose-50 text-slate-700 hover:text-rose-700 transition-colors text-sm font-semibold"
          data-testid="blog-vote-down"
          aria-label="Non, cet article ne m'a pas aidé"
        >
          <ThumbsDown size={14} /> Pas vraiment
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-3">Anonyme · Aucune donnée envoyée à un tiers</p>
    </div>
  );
}
